import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { KpiCard } from "@/components/common/KpiCard";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Mail, Calendar, Link2, Lock } from "lucide-react";

export default function UsageMetrics() {
  const { can } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["usage-metrics"], queryFn: () => get("/settings/integrations/usage-metrics"), enabled: can("settings", "override") });

  if (!can("settings", "override")) {
    return <EmptyState icon={Lock} title="Super Admin only" description="You don't have access to this page." />;
  }
  if (isLoading || !data) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Usage Metrics" subtitle="Google mailbox and calendar integration activity." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard testid="metric-mail" label="Mail Synced" value={data.mail_synced_count} icon={Mail} accent="blue" />
        <KpiCard testid="metric-accounts" label="Connected Accounts" value={data.connected_accounts_count} icon={Link2} accent="violet" />
        <KpiCard testid="metric-events" label="Calendar Events Synced" value={data.calendar_events_synced_count} icon={Calendar} accent="green" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Mail Synced (last 30 days)">
          <div className="p-4" style={{ height: 240 }}>
            {!data.mail_series.length ? <EmptyState icon={Mail} title="No data yet" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.mail_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Section>

        <Section title="Calendar Events Synced (last 30 days)">
          <div className="p-4" style={{ height: 240 }}>
            {!data.event_series.length ? <EmptyState icon={Calendar} title="No data yet" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.event_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#22C55E" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Section>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">AI token-usage metrics will appear here once a summarization provider is configured.</p>
    </div>
  );
}
